import React, { useState, useMemo } from 'react';
import { MapPin, ArrowRight, Filter, Package, Phone, ShieldCheck, Star, Clock, Truck as TruckIcon, Plus, Search, Upload } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { generateLoads, indianCities, truckTypes, materialTypes } from '../mock/mockData';
import { toast } from '../hooks/use-toast';
import { loadsApi } from '../lib/api';
import { useI18n } from '../lib/i18n';
import VoiceInput from '../components/VoiceInput';
import AiRateSuggest from '../components/AiRateSuggest';
import RouteInfoCard from '../components/RouteInfoCard';
import BulkUploadDialog from '../components/BulkUploadDialog';

const formatINR = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);
const timeAgo = (m) => m < 60 ? `${m}m ago` : m < 1440 ? `${Math.floor(m/60)}h ago` : `${Math.floor(m/1440)}d ago`;

// Adapts backend load shape to UI shape
const adaptLoad = (l) => ({
  id: l.id,
  origin: l.origin,
  destination: l.destination,
  material: l.material,
  weight: l.weight,
  truckType: l.truck_type,
  truckCode: l.truck_code,
  rate: l.rate,
  distance: l.distance,
  poster: l.poster_company,
  posterContact: l.poster_name,
  posterPhone: l.poster_phone,
  posterRating: l.poster_rating,
  verified: l.verified,
  postedMinutesAgo: Math.max(1, Math.floor((Date.now() - new Date(l.created_at).getTime()) / 60000)),
  paymentTerms: l.payment_terms,
  status: l.status,
});

export default function LoadBoard() {
  const [loads, setLoads] = useState(() => generateLoads(30));
  const [filters, setFilters] = useState({ origin: '', dest: '', truck: '', search: '' });
  const [postOpen, setPostOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [newLoad, setNewLoad] = useState({ origin: '', destination: '', material: '', weight: '', truckType: '', rate: '', paymentTerms: '', notes: '' });
  const { lang } = useI18n();

  const handleVoice = (r) => {
    const patch = {};
    if (r.origin) patch.origin = r.origin;
    if (r.destination) patch.destination = r.destination;
    if (r.material) patch.material = r.material;
    if (r.weight) patch.weight = r.weight;
    if (r.rate) patch.rate = r.rate;
    setNewLoad(prev => ({ ...prev, ...patch }));
    const filled = Object.keys(patch).length;
    toast({ title: 'Voice input applied', description: filled ? `Filled ${filled} field${filled > 1 ? 's' : ''}` : 'Nothing recognized. Try again slowly.' });
  };

  React.useEffect(() => {
    loadsApi.list().then(data => {
      if (data && data.length > 0) {
        const apiLoads = data.map(adaptLoad);
        setLoads(prev => [...apiLoads, ...prev]);
      }
    }).catch(() => {});
  }, []);

  const filtered = useMemo(() => loads.filter(l => {
    const s = filters.search.toLowerCase();
    return (!filters.origin || l.origin === filters.origin) &&
      (!filters.dest || l.destination === filters.dest) &&
      (!filters.truck || l.truckCode === filters.truck) &&
      (!s || l.origin.toLowerCase().includes(s) || l.destination.toLowerCase().includes(s) || l.material.toLowerCase().includes(s));
  }), [loads, filters]);

  const post = async () => {
    if (!newLoad.origin || !newLoad.destination || !newLoad.rate) {
      toast({ title: 'Missing fields', description: 'Please fill origin, destination and rate.' });
      return;
    }
    const truck = truckTypes.find(t => t.code === newLoad.truckType) || truckTypes[0];
    const payload = {
      origin: newLoad.origin,
      destination: newLoad.destination,
      material: newLoad.material || 'General',
      weight: `${newLoad.weight || '10'} T`,
      truck_type: truck.label,
      truck_code: truck.code,
      rate: parseInt(newLoad.rate),
      distance: Math.floor(Math.random() * 1500) + 300,
      payment_terms: newLoad.paymentTerms || 'Advance 30%',
      notes: newLoad.notes,
    };
    try {
      const created = await loadsApi.create(payload);
      setLoads([adaptLoad(created), ...loads]);
      setPostOpen(false);
      setNewLoad({ origin: '', destination: '', material: '', weight: '', truckType: '', rate: '', paymentTerms: '', notes: '' });
      toast({ title: 'Load posted!', description: `${created.origin.split(',')[0]} → ${created.destination.split(',')[0]} is now live.` });
    } catch (err) {
      toast({ title: 'Failed to post', description: err?.response?.data?.detail || 'Please sign in first.' });
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl lg:text-3xl font-extrabold text-[#0B2545] font-[Poppins]">Load Board</h1>
          <p className="text-sm text-gray-500 mt-1">{filtered.length.toLocaleString('en-IN')} live loads across India</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setBulkOpen(true)} variant="outline" className="border-purple-200 text-purple-700 hover:bg-purple-50 hover:text-purple-800"><Upload className="w-4 h-4 mr-2" />Bulk Upload</Button>
          <Dialog open={postOpen} onOpenChange={setPostOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#00A651] hover:bg-[#008a43] text-white"><Plus className="w-4 h-4 mr-2" />Post a Load</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle className="flex items-center justify-between gap-3">
              <span>Post New Load</span>
              <VoiceInput onResult={handleVoice} cities={indianCities} materials={materialTypes} lang={lang === 'hi' ? 'hi-IN' : 'en-IN'} />
            </DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-2">
              <div>
                <Label className="text-sm">Origin *</Label>
                <Select value={newLoad.origin} onValueChange={v => setNewLoad({ ...newLoad, origin: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select city" /></SelectTrigger>
                  <SelectContent className="max-h-64">{indianCities.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">Destination *</Label>
                <Select value={newLoad.destination} onValueChange={v => setNewLoad({ ...newLoad, destination: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select city" /></SelectTrigger>
                  <SelectContent className="max-h-64">{indianCities.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">Material</Label>
                <Select value={newLoad.material} onValueChange={v => setNewLoad({ ...newLoad, material: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="max-h-64">{materialTypes.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">Weight (Tonnes)</Label>
                <Input type="number" placeholder="10" className="mt-1.5" value={newLoad.weight} onChange={e => setNewLoad({ ...newLoad, weight: e.target.value })} />
              </div>
              <div>
                <Label className="text-sm">Truck Type</Label>
                <Select value={newLoad.truckType} onValueChange={v => setNewLoad({ ...newLoad, truckType: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{truckTypes.map(t => <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">Rate (₹) *</Label>
                <Input type="number" placeholder="50000" className="mt-1.5" value={newLoad.rate} onChange={e => setNewLoad({ ...newLoad, rate: e.target.value })} />
              </div>
              <div className="col-span-2">
                <Label className="text-sm">Payment Terms</Label>
                <Select value={newLoad.paymentTerms} onValueChange={v => setNewLoad({ ...newLoad, paymentTerms: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {['Advance 30%', 'Advance 50%', 'On Delivery (COD)', 'Credit 15 days', 'Credit 30 days'].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="text-sm">Notes</Label>
                <Textarea placeholder="Additional details..." className="mt-1.5" value={newLoad.notes} onChange={e => setNewLoad({ ...newLoad, notes: e.target.value })} />
              </div>
              <div className="col-span-2">
                <AiRateSuggest
                  origin={newLoad.origin}
                  destination={newLoad.destination}
                  truckCode={newLoad.truckType}
                  material={newLoad.material}
                  weight={newLoad.weight ? parseFloat(newLoad.weight) : 10}
                  currentRate={newLoad.rate}
                  onApply={(r) => setNewLoad({ ...newLoad, rate: String(r) })}
                />
              </div>
              <div className="col-span-2">
                <RouteInfoCard origin={newLoad.origin} destination={newLoad.destination} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPostOpen(false)}>Cancel</Button>
              <Button onClick={post} className="bg-[#00A651] hover:bg-[#008a43] text-white">Post Load</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-gray-100 p-4">
        <div className="grid md:grid-cols-4 gap-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input placeholder="Search loads..." className="pl-9" value={filters.search} onChange={e => setFilters({ ...filters, search: e.target.value })} />
          </div>
          <Select value={filters.origin} onValueChange={v => setFilters({ ...filters, origin: v === 'all' ? '' : v })}>
            <SelectTrigger><SelectValue placeholder="Origin city" /></SelectTrigger>
            <SelectContent className="max-h-64"><SelectItem value="all">All origins</SelectItem>{indianCities.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.dest} onValueChange={v => setFilters({ ...filters, dest: v === 'all' ? '' : v })}>
            <SelectTrigger><SelectValue placeholder="Destination city" /></SelectTrigger>
            <SelectContent className="max-h-64"><SelectItem value="all">All destinations</SelectItem>{indianCities.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.truck} onValueChange={v => setFilters({ ...filters, truck: v === 'all' ? '' : v })}>
            <SelectTrigger><SelectValue placeholder="Truck type" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All trucks</SelectItem>{truckTypes.map(t => <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {/* Load Cards */}
      <div className="grid gap-3">
        {filtered.map(l => (
          <div key={l.id} className="bg-white rounded-2xl border border-gray-100 p-5 hover:border-[#00A651]/40 hover-lift">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className="font-mono text-xs text-gray-500">{l.id}</span>
                  {l.verified && <Badge className="bg-[#00A651]/10 text-[#00A651] border-0 text-[10px]"><ShieldCheck className="w-3 h-3 mr-1" />Verified</Badge>}
                  <Badge className="bg-gray-100 text-gray-600 border-0 text-[10px]"><Clock className="w-3 h-3 mr-1" />{timeAgo(l.postedMinutesAgo)}</Badge>
                </div>
                <div className="flex items-center gap-3 mb-3">
                  <div>
                    <p className="text-xs text-gray-500">Origin</p>
                    <p className="font-bold text-[#0B2545]">{l.origin}</p>
                  </div>
                  <div className="flex-1 flex items-center justify-center min-w-[60px]">
                    <div className="flex-1 border-t-2 border-dashed border-gray-300"></div>
                    <TruckIcon className="w-5 h-5 text-[#00A651] mx-2" />
                    <div className="flex-1 border-t-2 border-dashed border-gray-300"></div>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Destination</p>
                    <p className="font-bold text-[#0B2545]">{l.destination}</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded">📦 {l.material}</span>
                  <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded">⚖️ {l.weight}</span>
                  <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded">🚚 {l.truckType}</span>
                  <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded">📍 {l.distance} km</span>
                  <span className="bg-[#FFB800]/15 text-[#B37800] px-2 py-1 rounded">💰 {l.paymentTerms}</span>
                </div>
              </div>
              <div className="flex flex-col items-end gap-2 min-w-[180px]">
                <div className="text-right">
                  <p className="text-2xl font-extrabold text-[#00A651] font-[Poppins]">{formatINR(l.rate)}</p>
                  <p className="text-xs text-gray-500">₹{Math.round(l.rate/l.distance)}/km</p>
                </div>
                <div className="text-right w-full">
                  <p className="text-xs text-gray-500">{l.poster}</p>
                  <div className="flex items-center justify-end gap-1 text-xs"><Star className="w-3 h-3 text-[#FFB800] fill-[#FFB800]" />{l.posterRating}</div>
                </div>
                <div className="flex gap-2 w-full">
                  <Button size="sm" variant="outline" onClick={() => toast({ title: 'Calling shipper...', description: l.posterPhone })}><Phone className="w-4 h-4" /></Button>
                  <Button size="sm" className="flex-1 bg-[#00A651] hover:bg-[#008a43] text-white" onClick={() => toast({ title: 'Bid submitted!', description: `Your bid for ${l.id} was sent to ${l.poster}` })}>Book Load</Button>
                </div>
              </div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
            <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No loads match your filters. Try broadening your search.</p>
          </div>
        )}
      </div>

      <BulkUploadDialog open={bulkOpen} onClose={() => setBulkOpen(false)} onSuccess={(r) => {
        // Refresh loads list from API to include the newly bulk-uploaded loads
        loadsApi.list().then(data => { if (data && data.length > 0) setLoads(data.map(adaptLoad)); }).catch(() => {});
      }} />
    </div>
  );
}
